import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter
} from '@reduxjs/toolkit';

import { client } from '../../../../api/client';
import { addAlert } from '../alerts/alertSlice';
import { FamilyPrefixMapping } from '../../../../utilities/constants/familyPrefixConstants';
import { resolveInspectorForNode } from '../../../../utilities/inspectors/registry';

const nodeAdapter = createEntityAdapter();

const normalizeAttributeVisibilityDefaults = (attributes) => {
  if (!attributes || typeof attributes !== 'object') {
    return {};
  }
  return Object.entries(attributes).reduce((acc, [key, attr]) => {
    if (!attr || typeof attr !== 'object' || Array.isArray(attr)) {
      acc[key] = attr;
      return acc;
    }
    const userModified = attr.visibilityUserModified === true;
    acc[key] = {
      ...attr,
      isVisible: userModified ? !!attr.isVisible : true,
      visibilityUserModified: userModified,
    };
    return acc;
  }, {});
};

const clonePlain = (value) => {
  if (!value || typeof value !== 'object') {
    return value;
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return null;
  }
};

const stripTransientNodeFields = (node) => {
  const plainNode = clonePlain(node);
  if (!plainNode || typeof plainNode !== 'object') {
    return plainNode;
  }
  const { selected, dragging, positionAbsolute, style, ...rest } = plainNode;
  return rest;
};

const isInspectorlessDesignNode = (node) => {
  const symbolLabel = node?.data?.symbolLabel;
  return node?.type === 'boxNode'
    || node?.type === 'straightLineNode'
    || node?.type === 'symbolDrawerNode'
    || symbolLabel === 'Box'
    || symbolLabel === 'StraightLine'
    || symbolLabel === 'SymbolDrawer';
};

const isDesignSymbolLabel = (symbolLabel) => (
  symbolLabel === 'Box'
  || symbolLabel === 'StraightLine'
  || symbolLabel === 'SymbolDrawer'
);

// ** Async Thunk for Updating Node Attributes **
export const updateNodeAttributes = createAsyncThunk(
  'node/updateNodeAttributes',
  async ({ nodeId, newAttributes, replace = false }, { getState, dispatch }) => {
    const state = getState();
    if (newAttributes) {
      const node = (state.node.nodes || []).find((n) => n.id === nodeId);
      const existingAttributes = node?.data?.attributes || {};

      if (replace) {
        dispatch(setNodeAttributes({ nodeId, attributes: newAttributes, replace: true }));
        return;
      }

      const attributesToUpdate = Object.keys(newAttributes).reduce((acc, key) => {
        if (existingAttributes[key] !== newAttributes[key]) {
          acc[key] = newAttributes[key];
        }
        return acc;
      }, {});

      if (Object.keys(attributesToUpdate).length > 0) {
        dispatch(setNodeAttributes({ nodeId, attributes: { ...existingAttributes, ...attributesToUpdate } }));
      }
    }
  }
);

// ** Initial Redux State **
const initialState = nodeAdapter.getInitialState({
  nodes: [], 
  status: 'idle',
  selectedNodeId: null,
  selectedNodeIds: [],
  selectedEdgeIds: []
});

// ** Node Slice Reducers **
const getAttrTimestamp = (attr) => {
  if (!attr || typeof attr !== 'object') return 0;
  return Number(attr.updatedAt || attr.lastUpdatedAt || 0);
};

const nodeSlice = createSlice({
  name: 'node',
  initialState,
  reducers: {
    setNodesActionCreator(state, action) {
      let newNodes = action.payload;
      const shouldReplace = action.payload?.replace === true;
      if (typeof action.payload === 'object' && action.payload.nodes) {
        newNodes = action.payload.nodes;
      }
      if (!Array.isArray(newNodes)) {
        return;
      }
      if (newNodes.length === 0) {
        state.nodes = [];
        return;
      }
      if (shouldReplace) {
        state.nodes = newNodes.map(stripTransientNodeFields).filter(Boolean);
        return;
      }

      const existingById = new Map(
        state.nodes.map((node) => [node.id, node])
      );

      const mergedNodes = newNodes.map((rawIncomingNode) => {
        const incomingNode = stripTransientNodeFields(rawIncomingNode);
        if (!incomingNode || !incomingNode.id) {
          return incomingNode;
        }
        const existing = existingById.get(incomingNode.id);
        if (!existing) {
          const symbolLabel = incomingNode.data?.symbolLabel;
          const suggestedName = getNextSuggestedName(
            symbolLabel,
            state.nodes,
            incomingNode.data?.alias || incomingNode.data?.baseAlias || incomingNode.data?.variantAlias
          );
          const inspectorInfo = isInspectorlessDesignNode(incomingNode)
            ? { attributes: {} }
            : resolveInspectorForNode(incomingNode.data || {});
          const attributes = {
            ...(inspectorInfo.attributes || {}),
            ...(incomingNode.data?.attributes || {}),
          };

          if (!attributes.nombre) {
            attributes.nombre = {
              isVisible: true,
              value: suggestedName,
              label: 'Nombre',
            };
          } else {
            attributes.nombre = {
              ...attributes.nombre,
              value: normalizeSuggestedName(
                symbolLabel,
                attributes.nombre.value ?? suggestedName,
                incomingNode.data?.alias || incomingNode.data?.baseAlias || incomingNode.data?.variantAlias
              ),
              label: attributes.nombre.label || 'Nombre',
              isVisible: true,
            };
          }

          return {
            ...incomingNode,
            data: {
              ...(incomingNode.data || {}),
              attributes: normalizeAttributeVisibilityDefaults(attributes),
            },
          };
        }

        const mergedData = {
          ...(existing.data || {}),
          ...(incomingNode.data || {}),
          attributes: (() => {
            const incomingAttrs = incomingNode.data?.attributes;
            if (incomingAttrs && Object.keys(incomingAttrs).length > 0) {
              return {
                ...(existing.data?.attributes || {}),
                ...Object.entries(incomingAttrs).reduce((acc, [attrKey, incomingAttr]) => {
                  const existingAttr = existing.data?.attributes?.[attrKey];
                  if (!existingAttr) {
                    acc[attrKey] = incomingAttr;
                    return acc;
                  }
                  const incomingTs = getAttrTimestamp(incomingAttr);
                  const existingTs = getAttrTimestamp(existingAttr);
                  if (existingTs && (!incomingTs || incomingTs < existingTs)) {
                    acc[attrKey] = existingAttr;
                  } else {
                    acc[attrKey] = incomingAttr;
                  }
                  return acc;
                }, {}),
              };
            }
            return existing.data?.attributes || {};
          })(),
        };
        mergedData.attributes = normalizeAttributeVisibilityDefaults(mergedData.attributes);

        return {
          ...existing,
          ...incomingNode,
          data: mergedData,
        };
      });

      state.nodes = mergedNodes.map(stripTransientNodeFields).filter(Boolean);
    },

    addNode(state, action) {
      const incoming = action.payload.node;
      const { symbolLabel } = incoming.data || {};
      const suggestedName = getNextSuggestedName(
        symbolLabel,
        state.nodes,
        incoming.data?.alias || incoming.data?.baseAlias || incoming.data?.variantAlias
      );

      // avoid duplicate insert if node with same id already exists
      if (state.nodes.some(n => n.id === incoming.id)) {
        return;
      }

      const inspectorInfo = isInspectorlessDesignNode(incoming)
        ? { attributes: {} }
        : resolveInspectorForNode(incoming.data || {});
      const attributes = {
        ...(inspectorInfo.attributes || {}),
        ...(incoming.data?.attributes || {}),
      };

      if (!attributes.nombre) {
        attributes.nombre = {
          isVisible: true,
          value: suggestedName,
          label: 'Nombre',
        };
      } else {
        attributes.nombre = {
          ...attributes.nombre,
          value: normalizeSuggestedName(
            symbolLabel,
            attributes.nombre.value ?? suggestedName,
            incoming.data?.alias || incoming.data?.baseAlias || incoming.data?.variantAlias
          ),
          label: attributes.nombre.label || 'Nombre',
          isVisible: true,
        };
      }

      const newNode = stripTransientNodeFields({
        ...incoming,
        data: {
          ...incoming.data,
          attributes: normalizeAttributeVisibilityDefaults(attributes),
        },
      });

      state.nodes = [...state.nodes, newNode];
    },

    setNodeAttributes(state, action) {
      const { nodeId, attributes, replace } = action.payload;
      const nodeIndex = state.nodes.findIndex(node => node.id === nodeId);

      if (nodeIndex !== -1) {
        if (replace) {
          state.nodes[nodeIndex].data.attributes = normalizeAttributeVisibilityDefaults({ ...(attributes || {}) });
          return;
        }
        Object.keys(attributes).forEach((key) => {
          const incomingAttr = attributes[key];
          const existingAttr = state.nodes[nodeIndex].data.attributes[key];

          if (existingAttr) {
            const incomingTs = getAttrTimestamp(incomingAttr);
            const existingTs = getAttrTimestamp(existingAttr);
            if (existingTs && incomingTs && incomingTs < existingTs) {
              // keep newer existing attribute
              state.nodes[nodeIndex].data.attributes[key] = {
                ...existingAttr,
                updatedAt: existingTs,
              };
            } else {
              state.nodes[nodeIndex].data.attributes[key] = {
                ...existingAttr,
                ...incomingAttr,
                updatedAt: incomingTs || existingTs || Date.now(),
              };
            }
          } else {
            state.nodes[nodeIndex].data.attributes[key] = incomingAttr;
          }
        });
        state.nodes[nodeIndex].data.attributes = normalizeAttributeVisibilityDefaults(
          state.nodes[nodeIndex].data.attributes
        );
      }
    },

    updateAttributeVisibility(state, action) {
      const { nodeId, attributeName, isVisible } = action.payload;
      const nodeIndex = state.nodes.findIndex(node => node.id === nodeId);

      if (nodeIndex !== -1 && state.nodes[nodeIndex].data.attributes[attributeName]) {
        state.nodes[nodeIndex].data.attributes[attributeName].isVisible = isVisible;
        state.nodes[nodeIndex].data.attributes[attributeName].visibilityUserModified = true;
      }
    },

    updateAttributeValue(state, action) {
      const { nodeId, attributeName, value } = action.payload;
      const nodeIndex = state.nodes.findIndex(node => node.id === nodeId);

      if (nodeIndex !== -1 && state.nodes[nodeIndex].data.attributes[attributeName]) {
        state.nodes[nodeIndex].data.attributes[attributeName].value = value;
        state.nodes[nodeIndex].data.attributes[attributeName].userModified = true;
        state.nodes[nodeIndex].data.attributes[attributeName].updatedAt = Date.now();
      }
    },

    setNodeHideAttributeLabels(state, action) {
      const { nodeId, hideAttributeLabels } = action.payload || {};
      const nodeIndex = state.nodes.findIndex((node) => node.id === nodeId);
      if (nodeIndex === -1) return;
      const node = state.nodes[nodeIndex];
      const existingData = node.data || {};
      node.data = {
        ...existingData,
        hideAttributeLabels: !!hideAttributeLabels,
      };
    },
    setAllNodesHideAttributeLabels(state, action) {
      const nextValue = !!action.payload;
      state.nodes = state.nodes.map((node) => {
        if (!node || !node.data) return node;
        return {
          ...node,
          data: {
            ...node.data,
            hideAttributeLabels: nextValue,
          },
        };
      });
    },

    setCurrentNodeId(state, action) {
      state.selectedNodeId = action.payload;
    },

    toggleNodeSelection(state, action) {
      const nodeId = action.payload;
      if (state.selectedNodeIds.includes(nodeId)) {
        state.selectedNodeIds = state.selectedNodeIds.filter(id => id !== nodeId);
      } else {
        state.selectedNodeIds.push(nodeId);
      }
    },

    selectSingleNode(state, action) {
      state.selectedNodeIds = [action.payload];
    },

    deselectNode(state, action) {
      state.selectedNodeIds = state.selectedNodeIds.filter(id => id !== action.payload);
    },

    selectEdge(state, action) {
      const edgeId = action.payload;
      if (!state.selectedEdgeIds.includes(edgeId)) {
        state.selectedEdgeIds.push(edgeId);
      }
    },

    deselectEdge(state, action) {
      state.selectedEdgeIds = state.selectedEdgeIds.filter(id => id !== action.payload);
    },

    clearSelection(state) {
      state.selectedNodeId = null;
      state.selectedNodeIds = [];
      state.selectedEdgeIds = [];
      state.nodes = state.nodes.map(stripTransientNodeFields).filter(Boolean);
    },

    // Clear only node selection (keep edges intact)
    clearNodeSelection(state) {
      state.selectedNodeId = null;
      state.selectedNodeIds = [];
      state.nodes = state.nodes.map(stripTransientNodeFields).filter(Boolean);
    },

    updateNodeVariants(state, action) {
      const { nodeId, variants } = action.payload;
      const nodeIndex = state.nodes.findIndex(node => node.id === nodeId);
      if (nodeIndex !== -1) {
        state.nodes[nodeIndex].data.variants = variants;
      }
    },    

    // Bulk set selected node ids (e.g., from box selection)
    setSelectedNodeIds(state, action) {
      const ids = action.payload || [];
      state.selectedNodeIds = Array.isArray(ids) ? ids : [];
      // keep single selectedNodeId in sync (first or null)
      state.selectedNodeId = state.selectedNodeIds.length > 0 ? state.selectedNodeIds[0] : null;
      if (state.selectedNodeIds.length === 0) {
        state.nodes = state.nodes.map(stripTransientNodeFields).filter(Boolean);
      }
    },

    resetNodeSlice: () => initialState,
  }
});

// ** Export Actions **
export const {
  setNodeAttributes,
  updateAttributeValue,
  updateAttributeVisibility,
  setCurrentNodeId,
  setNodesActionCreator,
  addNode,
  toggleNodeSelection,
  selectSingleNode,
  deselectNode,
  selectEdge,
  deselectEdge,
  clearSelection,  
  clearNodeSelection,
  updateNodeVariants,
  setSelectedNodeIds,
  resetNodeSlice,
  setNodeHideAttributeLabels,
  setAllNodesHideAttributeLabels
} = nodeSlice.actions;

// ** Helper Functions **
const normalizeFamilyAlias = (value) => {
  if (typeof value !== 'string') return '';
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

function getFamilyPrefix(symbolLabel, alias = '') {
  const normalizedAlias = normalizeFamilyAlias(alias);
  if (symbolLabel === 'ElectricEnergySupplier') {
    if (
      normalizedAlias === 'generador-a' ||
      normalizedAlias === 'generador-solar' ||
      normalizedAlias === 'generador-eolico'
    ) {
      return 'GEN';
    }
  }
  if (symbolLabel === 'Generator' && normalizedAlias === 'contactos-trifasicos') {
    return 'CON';
  }
  if (symbolLabel === 'ElectromagneticSwitch' && normalizedAlias === 'restaurador') {
    return 'REST';
  }
  if (symbolLabel === 'Vfd' && normalizedAlias.includes('linea aerea')) {
    return 'LA';
  }
  return FamilyPrefixMapping[symbolLabel] || "GEN";
}

function getLegacyFamilyPrefixes(symbolLabel, alias = '') {
  const prefix = getFamilyPrefix(symbolLabel, alias);
  if (symbolLabel === 'ElectromagneticSwitch' && prefix === 'PCSC') {
    return ['CON'];
  }
  return [];
}

export function normalizeSuggestedName(symbolLabel, name, alias = '') {
  if (typeof name !== 'string') return name;
  const prefix = getFamilyPrefix(symbolLabel, alias);
  const legacyPrefixes = getLegacyFamilyPrefixes(symbolLabel, alias);
  const normalized = name.trim();
  for (const legacyPrefix of legacyPrefixes) {
    const match = normalized.match(new RegExp(`^${legacyPrefix}([/-].+)$`, 'i'));
    if (match) {
      return `${prefix}${match[1]}`;
    }
  }
  return normalized;
}

function padWithZeros(num, length = 4) {
  return String(num).padStart(length, "0");
}

export function getNextSuggestedName(symbolLabel, existingNodes, alias = '', seedName = null, reservedNameValues = []) {
  const prefix = getFamilyPrefix(symbolLabel, alias);
  const acceptedPrefixes = [prefix, ...getLegacyFamilyPrefixes(symbolLabel, alias)];
  const isDesignName = isDesignSymbolLabel(symbolLabel);
  const sourceNodes = Array.isArray(existingNodes) ? existingNodes : [];

  const familyNodes = sourceNodes.filter(
    (node) => {
      const nodeSymbolLabel = node.data?.symbolLabel;
      if (isDesignName) {
        return isDesignSymbolLabel(nodeSymbolLabel);
      }
      return nodeSymbolLabel?.toLowerCase() === symbolLabel.toLowerCase();
    }
  );

  const extractSequence = (name) => {
    if (typeof name !== 'string') return null;
    const normalized = name.trim();
    for (const acceptedPrefix of acceptedPrefixes) {
      const regex = new RegExp(`^${acceptedPrefix}-(\\d{1,})`, 'i');
      const match = normalized.match(regex);
      if (!match) continue;
      const value = parseInt(match[1], 10);
      return Number.isFinite(value) ? value : null;
    }
    return null;
  };

  const collectCandidateStrings = (node) => {
    const attributes = node?.data?.attributes || {};
    const potentials = [
      attributes?.nombre?.value,
      attributes?.name?.value,
      node?.data?.name,
      node?.data?.label,
    ];
    return potentials.filter((val) => typeof val === 'string');
  };

  let highestSequence = isDesignName ? -1 : 0;
  const reservedNames = new Set();

  familyNodes.forEach((node) => {
    collectCandidateStrings(node).forEach((value) => {
      const sequence = extractSequence(value);
      if (sequence !== null && sequence > highestSequence) {
        highestSequence = sequence;
      }
      if (typeof value === 'string') {
        reservedNames.add(value.trim().toLowerCase());
      }
    });
  });

  const reservedValues = Array.isArray(reservedNameValues)
    ? reservedNameValues
    : Array.from(reservedNameValues || []);
  reservedValues.forEach((value) => {
    if (typeof value !== 'string') return;
    const normalized = value.trim();
    if (!normalized) return;
    const sequence = extractSequence(normalized);
    if (sequence !== null && sequence > highestSequence) {
      highestSequence = sequence;
    }
    reservedNames.add(normalized.toLowerCase());
  });

  const buildIncrementedDelimitedName = (name) => {
    if (typeof name !== 'string') return null;
    const normalized = normalizeSuggestedName(symbolLabel, name, alias);
    if (!normalized) return null;
    const match = normalized.match(/^(.*[/-])(\d+)$/);
    if (!match) return null;
    const stem = match[1];
    const numericText = match[2];
    const initialValue = parseInt(numericText, 10);
    if (!Number.isFinite(initialValue)) return null;

    let nextValue = initialValue + 1;
    while (true) {
      const candidate = `${stem}${String(nextValue).padStart(numericText.length, '0')}`;
      const lowered = candidate.toLowerCase();
      const conflict = Array.from(reservedNames).some((existing) => existing.startsWith(lowered));
      if (!conflict) {
        return candidate;
      }
      nextValue += 1;
    }
  };

  const incrementedSeedName = buildIncrementedDelimitedName(seedName);
  if (incrementedSeedName) {
    return incrementedSeedName;
  }

  const familySeed = familyNodes
    .flatMap((node) => collectCandidateStrings(node))
    .find((value) => typeof value === 'string' && /^(.*[/-])(\d+)$/.test(value.trim()));

  const incrementedFamilySeed = buildIncrementedDelimitedName(familySeed);
  if (incrementedFamilySeed) {
    return incrementedFamilySeed;
  }

  let nextNumber = highestSequence + 1;
  let candidate;
  while (true) {
    candidate = `${prefix}-${padWithZeros(nextNumber, isDesignName ? 3 : 4)}`;
    const lowered = candidate.toLowerCase();
    const conflict = Array.from(reservedNames).some((existing) =>
      existing.startsWith(lowered)
    );
    if (!conflict) {
      break;
    }
    nextNumber += 1;
  }

  return candidate;
}

// ** Export Reducer **
export default nodeSlice.reducer;
